import { X, CheckCircle2, AlertTriangle, XCircle, Zap, Flag, Clock } from 'lucide-react';
import type { CanvasStep } from './types';
import { STEP_META, getStepStatus } from './types';
import { InputMappingEditor } from './InputMappingEditor';
import styles from './WorkflowCanvas.module.css';

function getSchemaProperties(inputSchema: any): string[] {
  if (!inputSchema) return [];
  try {
    const schema = typeof inputSchema === 'string' ? JSON.parse(inputSchema) : inputSchema;
    if (schema && typeof schema === 'object') {
      if (schema.properties) {
        return Object.keys(schema.properties);
      }
    }
  } catch (e) {
    console.error('Error parsing inputSchema:', e);
  }
  return [];
}

interface StepConfigPanelProps {
  step: CanvasStep | null;
  isTriggerSelected?: boolean;
  isEndSelected?: boolean;
  maxExecutionTime?: number | null;
  onChangeMaxExecutionTime?: (val: number | null) => void;
  triggerConfig?: any;
  onUpdateTrigger?: (patch: any) => void;
  availableConnectors?: any[];
  allSteps: CanvasStep[];
  availableTools: { toolName: string; displayName: string; inputSchema?: any }[];
  availableSkills: { name: string; id: string; inputSchema?: any }[];
  onUpdate: (id: string, patch: Partial<CanvasStep>) => void;
  onClose: () => void;
}

type DurationUnit = 'ms' | 's' | 'm' | 'h';
const UNIT_MS: Record<DurationUnit, number> = { ms: 1, s: 1000, m: 60000, h: 3600000 };

function msToDisplay(ms: number): { value: number; unit: DurationUnit } {
  if (ms % 3600000 === 0 && ms >= 3600000) return { value: ms / 3600000, unit: 'h' };
  if (ms % 60000 === 0 && ms >= 60000) return { value: ms / 60000, unit: 'm' };
  if (ms % 1000 === 0 && ms >= 1000) return { value: ms / 1000, unit: 's' };
  return { value: ms, unit: 'ms' };
}

export function StepConfigPanel({
  step,
  isTriggerSelected = false,
  isEndSelected = false,
  maxExecutionTime,
  onChangeMaxExecutionTime,
  triggerConfig,
  onUpdateTrigger,
  availableConnectors = [],
  allSteps,
  availableTools,
  availableSkills,
  onUpdate,
  onClose,
}: StepConfigPanelProps) {
  const isOpen = step !== null || isTriggerSelected || isEndSelected;
  const meta = step
    ? STEP_META[step.type]
    : isTriggerSelected
      ? { Icon: Zap, label: 'TriggerConfig', color: '#22c55e' }
      : isEndSelected
        ? { Icon: Flag, label: 'Workflow Settings', color: '#6366f1' }
        : null;

  const toolNames = availableTools.map((t) => t.toolName);
  const skillNames = availableSkills.map((s) => s.name);

  const status = step
    ? getStepStatus(step, toolNames, skillNames)
    : isTriggerSelected && triggerConfig
      ? (triggerConfig.type ? 'configured' : 'unconfigured')
      : null;

  function patch(p: Partial<CanvasStep>) {
    if (step) onUpdate(step.id, p);
  }

  // Duration helpers
  const dur = step?.waitDurationMs !== undefined ? msToDisplay(step.waitDurationMs) : { value: 0, unit: 'ms' as DurationUnit };

  function setDuration(value: number, unit: DurationUnit) {
    patch({ waitDurationMs: value * UNIT_MS[unit] });
  }

  function iconBgClass() {
    if (isTriggerSelected) return styles.iconSkill;
    if (isEndSelected) return styles.iconWait;
    if (!step) return '';
    switch (step.type) {
      case 'wait':       return styles.iconWait;
      case 'tool':       return styles.iconTool;
      case 'skill':      return styles.iconSkill;
      case 'condition':  return styles.iconCondition;
      case 'callback':   return styles.iconCallback;
      case 'emit_event': return styles.iconEmitEvent;
      case 'wait_event': return styles.iconWaitEvent;
      case 'loop':       return styles.iconLoop;
      default:           return '';
    }
  }

  return (
    <div className={styles.panelOverlay} style={isOpen ? { display: 'block' } : { display: 'none' }}>
      <div className={`${styles.configPanel} ${isOpen ? styles.open : ''}`} role="dialog" aria-modal="true" aria-label="Step configuration">
        {/* Header */}
        <div className={styles.panelHeader}>
          <div className={styles.panelTitleRow}>
            <div
              className={`${styles.panelTypeIcon} ${iconBgClass()}`}
              style={{ color: meta?.color }}
            >
              {meta && <meta.Icon size={18} strokeWidth={2} />}
            </div>
            <div>
              <h2 className={styles.panelTitle}>
                {isTriggerSelected ? 'Workflow Start Trigger' : isEndSelected ? 'Workflow Settings' : `${meta?.label ?? ''} Step`}
              </h2>
              {step && <p className={styles.panelSubtitle} id={`panel-id-${step.id}`}>Step {allSteps.findIndex((s) => s.id === step.id) + 1} — <code style={{ fontSize: '0.72rem' }}>{step.id}</code></p>}
              {isTriggerSelected && <p className={styles.panelSubtitle}>Configure how execution is initiated</p>}
              {isEndSelected && <p className={styles.panelSubtitle}>Global settings applied to every execution</p>}
            </div>
          </div>
          <button className={styles.panelCloseBtn} onClick={onClose} aria-label="Close panel">
            <X size={18} />
          </button>
        </div>

        {/* Body */}
        <div className={styles.panelBody}>
          {isTriggerSelected && triggerConfig && onUpdateTrigger && (
            <>
              {/* Status */}
              <div className={`${styles.panelStatus} ${styles.panelStatusGreen}`}>
                <CheckCircle2 size={14} />
                Trigger source configured ({triggerConfig.type ?? 'manual'})
              </div>

              {/* Trigger Type selection */}
              <div className={styles.fieldGroup}>
                <label className={styles.fieldLabel} htmlFor="trigger-type">
                  Trigger Type <span className={styles.fieldRequired}>*</span>
                </label>
                <select
                  id="trigger-type"
                  className={styles.fieldSelect}
                  value={triggerConfig.type ?? 'manual'}
                  onChange={(e) => onUpdateTrigger({ type: e.target.value })}
                >
                  <option value="manual">Manual Execution (API/UI)</option>
                  <option value="event">Event Trigger (PubSub/Kafka/etc.)</option>
                  <option value="schedule">Scheduled Cron Job</option>
                  <option value="webhook">Webhook Endpoint</option>
                </select>
                <p className={styles.fieldHint}>Define how executions of this workflow are initiated.</p>
              </div>

              <div className={styles.sectionDivider} />

              {/* Event Trigger Form */}
              {triggerConfig.type === 'event' && (
                <>
                  <div className={styles.fieldGroup}>
                    <label className={styles.fieldLabel} htmlFor="trigger-connector">
                      Event Connector
                    </label>
                    <select
                      id="trigger-connector"
                      className={styles.fieldSelect}
                      value={triggerConfig.connectorId ?? ''}
                      onChange={(e) => onUpdateTrigger({ connectorId: e.target.value })}
                    >
                      <option value="">— Select Event Connector —</option>
                      {availableConnectors.map((c: any) => (
                        <option key={c.id} value={c.id}>
                          {c.name} ({c.provider})
                        </option>
                      ))}
                    </select>
                    <p className={styles.fieldHint}>Select the source connector emitting the events.</p>
                  </div>

                  <div className={styles.fieldGroup}>
                    <label className={styles.fieldLabel} htmlFor="trigger-event-type">
                      Event Type <span className={styles.fieldRequired}>*</span>
                    </label>
                    <input
                      id="trigger-event-type"
                      className={styles.fieldInput}
                      value={triggerConfig.eventType ?? ''}
                      onChange={(e) => onUpdateTrigger({ eventType: e.target.value })}
                      placeholder="e.g. ride.started"
                    />
                    <p className={styles.fieldHint}>Filter by a specific event type name.</p>
                  </div>

                  <div className={styles.fieldGroup}>
                    <label className={styles.fieldLabel} htmlFor="trigger-correlation-key">
                      Correlation Key <span className={styles.fieldRequired}>*</span>
                    </label>
                    <input
                      id="trigger-correlation-key"
                      className={styles.fieldInput}
                      value={triggerConfig.correlationKey ?? ''}
                      onChange={(e) => onUpdateTrigger({ correlationKey: e.target.value })}
                      placeholder="e.g. rideId"
                    />
                    <p className={styles.fieldHint}>The payload field name to extract as correlation ID (e.g. "rideId").</p>
                  </div>

                  <InputMappingEditor
                    rows={Object.entries(triggerConfig.filter ?? {}).map(([k, v]) => ({ key: k, value: v as string }))}
                    onChange={(rows) => {
                      const filterObj = Object.fromEntries(rows.map(r => [r.key, r.value]));
                      onUpdateTrigger({ filter: filterObj });
                    }}
                    title="Payload Filter Matches"
                    knownParams={(() => {
                      const conn = availableConnectors.find((c) => c.id === triggerConfig.connectorId);
                      return conn ? getSchemaProperties(conn.schema) : [];
                    })()}
                  />
                </>
              )}

              {/* Schedule Trigger Form */}
              {triggerConfig.type === 'schedule' && (
                <div className={styles.fieldGroup}>
                  <label className={styles.fieldLabel} htmlFor="trigger-cron">
                    Cron Expression <span className={styles.fieldRequired}>*</span>
                  </label>
                  <input
                    id="trigger-cron"
                    className={styles.fieldInput}
                    value={triggerConfig.cronExpression ?? ''}
                    onChange={(e) => onUpdateTrigger({ cronExpression: e.target.value })}
                    placeholder="e.g. */5 * * * *"
                  />
                  <p className={styles.fieldHint}>Standard 5-field cron syntax (min, hour, dom, month, dow).</p>
                </div>
              )}

              {/* Webhook Trigger Form */}
              {triggerConfig.type === 'webhook' && (
                <div className={styles.fieldGroup}>
                  <label className={styles.fieldLabel} htmlFor="trigger-webhook-url">
                    Webhook URL
                  </label>
                  <input
                    id="trigger-webhook-url"
                    className={styles.fieldInput}
                    style={{ background: 'var(--color-bg-dim)', color: 'var(--color-text-muted)', cursor: 'default' }}
                    value={`${window.location.origin}/v1/workflows/webhooks/trigger`}
                    readOnly
                    onClick={(e) => (e.target as HTMLInputElement).select()}
                  />
                  <p className={styles.fieldHint}>The URL where POST requests should be directed to run this workflow.</p>
                </div>
              )}
            </>
          )}

          {/* ── END NODE (Workflow Settings) ──────────────────── */}
          {isEndSelected && (
            <>
              <div className={`${styles.panelStatus} ${styles.panelStatusGreen}`}>
                <CheckCircle2 size={14} />
                Workflow end — configure global execution settings
              </div>

              <div className={styles.fieldGroup}>
                <label className={styles.fieldLabel} htmlFor="end-max-exec-time">
                  <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <Clock size={13} />
                    Maximum Execution Time (seconds)
                  </span>
                </label>
                <input
                  id="end-max-exec-time"
                  type="number"
                  min={0}
                  className={styles.fieldInput}
                  value={maxExecutionTime ?? ''}
                  placeholder="No limit (leave blank)"
                  onChange={(e) => {
                    const v = e.target.value === '' ? null : Number(e.target.value);
                    onChangeMaxExecutionTime?.(v);
                  }}
                />
                <p className={styles.fieldHint}>
                  If an execution runs longer than this, it will be automatically aborted and a platform alert will be raised.
                  Leave blank for no limit.
                </p>
              </div>
            </>
          )}

          {!step ? null : (
            <>
              {/* Status */}
              <div
                className={`${styles.panelStatus} ${
                  status === 'configured'
                    ? styles.panelStatusGreen
                    : status === 'warning'
                      ? styles.panelStatusYellow
                      : styles.panelStatusRed
                }`}
              >
                {status === 'configured'
                  ? <CheckCircle2 size={14} />
                  : status === 'warning'
                    ? <AlertTriangle size={14} />
                    : <XCircle size={14} />}
                {status === 'configured'
                  ? 'Step is fully configured'
                  : status === 'warning'
                    ? 'Warning: a referenced resource was not found'
                    : 'Required fields missing — step will not execute'}
              </div>

              {/* Name */}
              <div className={styles.fieldGroup}>
                <label className={styles.fieldLabel} htmlFor={`step-name-${step.id}`}>
                  Step Name
                </label>
                <input
                  id={`step-name-${step.id}`}
                  className={styles.fieldInput}
                  value={step.name}
                  onChange={(e) => patch({ name: e.target.value })}
                  placeholder="Descriptive step name"
                />
              </div>

              <div className={styles.sectionDivider} />

              {/* ── WAIT ─────────────────────────────────────── */}
              {step.type === 'wait' && (
                <div className={styles.fieldGroup}>
                  <label className={styles.fieldLabel} htmlFor={`dur-val-${step.id}`}>
                    Wait Duration <span className={styles.fieldRequired}>*</span>
                  </label>
                  <div className={styles.durationRow}>
                    <input
                      id={`dur-val-${step.id}`}
                      type="number"
                      min={0}
                      className={`${styles.fieldInput} ${styles.durationInput}`}
                      value={dur.value}
                      onChange={(e) => setDuration(Number(e.target.value), dur.unit)}
                    />
                    <select
                      className={`${styles.fieldSelect} ${styles.unitSelect}`}
                      value={dur.unit}
                      onChange={(e) => setDuration(dur.value, e.target.value as DurationUnit)}
                      aria-label="Duration unit"
                    >
                      <option value="ms">ms</option>
                      <option value="s">seconds</option>
                      <option value="m">minutes</option>
                      <option value="h">hours</option>
                    </select>
                  </div>
                  <p className={styles.fieldHint}>
                    Workflow pauses for this duration before proceeding. ({step.waitDurationMs?.toLocaleString()} ms)
                  </p>
                </div>
              )}

              {/* ── TOOL ─────────────────────────────────────── */}
              {step.type === 'tool' && (
                <>
                  <div className={styles.fieldGroup}>
                    <label className={styles.fieldLabel} htmlFor={`tool-name-${step.id}`}>
                      Tool <span className={styles.fieldRequired}>*</span>
                    </label>
                    {availableTools.length > 0 ? (
                      <select
                        id={`tool-name-${step.id}`}
                        className={styles.fieldSelect}
                        value={step.toolName ?? ''}
                        onChange={(e) => patch({ toolName: e.target.value })}
                      >
                        <option value="">— Select a tool —</option>
                        {availableTools.map((t) => (
                          <option key={t.toolName} value={t.toolName}>
                            {t.displayName} ({t.toolName})
                          </option>
                        ))}
                      </select>
                    ) : (
                      <input
                        id={`tool-name-${step.id}`}
                        className={styles.fieldInput}
                        value={step.toolName ?? ''}
                        onChange={(e) => patch({ toolName: e.target.value })}
                        placeholder="tool_name (as registered)"
                      />
                    )}
                    <p className={styles.fieldHint}>The registered tool name to execute at this step.</p>
                  </div>

                  <InputMappingEditor
                    rows={step.inputMapping ?? []}
                    onChange={(rows) => patch({ inputMapping: rows })}
                    knownParams={(() => {
                      const tool = availableTools.find((t) => t.toolName === step.toolName);
                      return tool ? getSchemaProperties(tool.inputSchema) : [];
                    })()}
                  />
                </>
              )}

              {/* ── SKILL ────────────────────────────────────── */}
              {step.type === 'skill' && (
                <>
                  <div className={styles.fieldGroup}>
                    <label className={styles.fieldLabel} htmlFor={`skill-name-${step.id}`}>
                      Skill <span className={styles.fieldRequired}>*</span>
                    </label>
                    {availableSkills.length > 0 ? (
                      <select
                        id={`skill-name-${step.id}`}
                        className={styles.fieldSelect}
                        value={step.skillId ?? ''}
                        onChange={(e) => {
                          const s = availableSkills.find((sk) => sk.id === e.target.value);
                          patch({ skillId: e.target.value, skillName: s?.name ?? '' });
                        }}
                      >
                        <option value="">— Select a skill —</option>
                        {availableSkills.map((sk) => (
                          <option key={sk.id} value={sk.id}>
                            {sk.name}
                          </option>
                        ))}
                      </select>
                    ) : (
                      <input
                        id={`skill-name-${step.id}`}
                        className={styles.fieldInput}
                        value={step.skillName ?? ''}
                        onChange={(e) => patch({ skillName: e.target.value })}
                        placeholder="skill-name"
                      />
                    )}
                  </div>

                  <InputMappingEditor
                    rows={step.inputMapping ?? []}
                    onChange={(rows) => patch({ inputMapping: rows })}
                    knownParams={(() => {
                      const skill = availableSkills.find((s) => s.id === step.skillId);
                      return skill ? getSchemaProperties(skill.inputSchema) : [];
                    })()}
                  />
                </>
              )}

              {/* ── CONDITION ────────────────────────────────── */}
              {step.type === 'condition' && (
                <>
                  <div className={styles.fieldGroup}>
                    <label className={styles.fieldLabel} htmlFor={`cond-expr-${step.id}`}>
                      Condition Expression <span className={styles.fieldRequired}>*</span>
                    </label>
                    <textarea
                      id={`cond-expr-${step.id}`}
                      className={styles.fieldTextarea}
                      value={step.conditionExpression ?? ''}
                      onChange={(e) => patch({ conditionExpression: e.target.value })}
                      placeholder="e.g. steps.tool_1.output.status === 'success'"
                      rows={3}
                    />
                    <p className={styles.fieldHint}>JavaScript expression evaluated against step state.</p>
                  </div>

                  <div className={styles.fieldGroup}>
                    <label className={styles.fieldLabel} htmlFor={`on-success-${step.id}`}>
                      On True → Step ID <span className={styles.fieldRequired}>*</span>
                    </label>
                    <select
                      id={`on-success-${step.id}`}
                      className={styles.fieldSelect}
                      value={step.onSuccess ?? ''}
                      onChange={(e) => patch({ onSuccess: e.target.value })}
                    >
                      <option value="">— Select next step —</option>
                      {allSteps.filter((s) => s.id !== step.id).map((s) => {
                        const idx = allSteps.findIndex((x) => x.id === s.id);
                        return (
                          <option key={s.id} value={s.id}>
                            {s.name} (Step {idx + 1} - ID: {s.id})
                          </option>
                        );
                      })}
                    </select>
                  </div>

                  <div className={styles.fieldGroup}>
                    <label className={styles.fieldLabel} htmlFor={`on-failure-${step.id}`}>
                      On False → Step ID <span className={styles.fieldRequired}>*</span>
                    </label>
                    <select
                      id={`on-failure-${step.id}`}
                      className={styles.fieldSelect}
                      value={step.onFailure ?? ''}
                      onChange={(e) => patch({ onFailure: e.target.value })}
                    >
                      <option value="">— Select next step —</option>
                      {allSteps.filter((s) => s.id !== step.id).map((s) => {
                        const idx = allSteps.findIndex((x) => x.id === s.id);
                        return (
                          <option key={s.id} value={s.id}>
                            {s.name} (Step {idx + 1} - ID: {s.id})
                          </option>
                        );
                      })}
                    </select>
                  </div>
                </>
              )}

              {/* ── CALLBACK ─────────────────────────────────── */}
              {step.type === 'callback' && (
                <>
                  <div className={styles.fieldGroup}>
                    <label className={styles.fieldLabel} htmlFor={`cb-key-${step.id}`}>
                      Callback Key <span className={styles.fieldRequired}>*</span>
                    </label>
                    <input
                      id={`cb-key-${step.id}`}
                      className={styles.fieldInput}
                      value={step.callbackKey ?? ''}
                      onChange={(e) => patch({ callbackKey: e.target.value })}
                      placeholder="e.g. payment_confirmed"
                    />
                    <p className={styles.fieldHint}>
                      Unique key sent in the webhook callback to resume this workflow.
                    </p>
                  </div>

                  <div className={styles.fieldGroup}>
                    <label className={styles.fieldLabel} htmlFor={`cb-timeout-${step.id}`}>
                      Timeout
                    </label>
                    <div className={styles.durationRow}>
                      <input
                        id={`cb-timeout-${step.id}`}
                        type="number"
                        min={0}
                        className={`${styles.fieldInput} ${styles.durationInput}`}
                        value={Math.round((step.timeoutMs ?? 86400000) / 3600000)}
                        onChange={(e) => patch({ timeoutMs: Number(e.target.value) * 3600000 })}
                      />
                      <span
                        className={styles.fieldSelect}
                        style={{ width: 90, display: 'flex', alignItems: 'center', paddingLeft: '0.85rem', color: 'var(--color-text-muted)' }}
                      >
                        hours
                      </span>
                    </div>
                    <p className={styles.fieldHint}>
                      Workflow times out and moves to error if no callback arrives within this window.
                    </p>
                  </div>
                </>
              )}
              {/* ── WAIT EVENT ───────────────────────────────── */}
              {step.type === 'wait_event' && (
                <>
                  <div className={styles.fieldGroup}>
                    <label className={styles.fieldLabel} htmlFor={`we-type-${step.id}`}>
                      Event Type <span className={styles.fieldRequired}>*</span>
                    </label>
                    <input
                      id={`we-type-${step.id}`}
                      className={styles.fieldInput}
                      value={step.waitEventType ?? ''}
                      onChange={(e) => patch({ waitEventType: e.target.value })}
                      placeholder="e.g. ride.payment_received"
                    />
                    <p className={styles.fieldHint}>
                      Workflow pauses here until this event arrives for the same correlation ID.
                    </p>
                  </div>
                  <div className={styles.fieldGroup}>
                    <label className={styles.fieldLabel} htmlFor={`we-timeout-${step.id}`}>Timeout (hours)</label>
                    <div className={styles.durationRow}>
                      <input
                        id={`we-timeout-${step.id}`}
                        type="number" min={0}
                        className={`${styles.fieldInput} ${styles.durationInput}`}
                        value={Math.round((step.waitEventTimeoutMs ?? 86400000) / 3600000)}
                        onChange={(e) => patch({ waitEventTimeoutMs: Number(e.target.value) * 3600000 })}
                      />
                      <span className={styles.fieldSelect} style={{ width: 90, display: 'flex', alignItems: 'center', paddingLeft: '0.85rem', color: 'var(--color-text-muted)' }}>hours</span>
                    </div>
                  </div>
                </>
              )}

              {/* ── EMIT EVENT ───────────────────────────────── */}
              {step.type === 'emit_event' && (
                <>
                  <div className={styles.fieldGroup}>
                    <label className={styles.fieldLabel} htmlFor={`ee-type-${step.id}`}>
                      Event Type <span className={styles.fieldRequired}>*</span>
                    </label>
                    <input
                      id={`ee-type-${step.id}`}
                      className={styles.fieldInput}
                      value={step.eventType ?? ''}
                      onChange={(e) => patch({ eventType: e.target.value })}
                      placeholder="e.g. ride.completed"
                    />
                    <p className={styles.fieldHint}>Event type string published to downstream systems.</p>
                  </div>
                  <InputMappingEditor
                    rows={step.eventPayloadMapping ?? []}
                    onChange={(rows) => patch({ eventPayloadMapping: rows })}
                    title="Event Payload Mapping"
                    knownParams={(() => {
                      const conn = availableConnectors.find((c) => c.id === step.connectorId);
                      return conn ? getSchemaProperties(conn.schema) : [];
                    })()}
                  />
                </>
              )}

              {/* ── LOOP ─────────────────────────────────────── */}
              {step.type === 'loop' && (
                <>
                  <div className={styles.fieldGroup}>
                    <label className={styles.fieldLabel} htmlFor={`loop-target-${step.id}`}>
                      Jump Back To <span className={styles.fieldRequired}>*</span>
                    </label>
                    <select
                      id={`loop-target-${step.id}`}
                      className={styles.fieldSelect}
                      value={step.targetStepId ?? ''}
                      onChange={(e) => patch({ targetStepId: e.target.value })}
                    >
                      <option value="">— Select step to loop to —</option>
                      {allSteps.filter((s) => s.id !== step.id && s.type !== 'loop' && s.type !== 'condition').map((s) => {
                        const idx = allSteps.findIndex((x) => x.id === s.id);
                        return (
                          <option key={s.id} value={s.id}>
                            {s.name} (Step {idx + 1} - ID: {s.id})
                          </option>
                        );
                      })}
                    </select>
                    <p className={styles.fieldHint}>When reached, execution jumps back to this step.</p>
                  </div>
                  <div className={styles.fieldGroup}>
                    <label className={styles.fieldLabel} htmlFor={`loop-max-${step.id}`}>
                      Max Iterations
                    </label>
                    <input
                      id={`loop-max-${step.id}`}
                      type="number" min={1} max={100}
                      className={styles.fieldInput}
                      value={step.maxIterations ?? 10}
                      onChange={(e) => patch({ maxIterations: Number(e.target.value) })}
                    />
                    <p className={styles.fieldHint}>Safety cap — loop exits when this count is reached.</p>
                  </div>
                </>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
